/**
 * GFG-273 (Campaigns tab, GFG Admin) — the table's column definitions: the three fixed columns,
 * pinned left and kept out of the Columns popover, plus the thirteen configurable ones keyed by
 * the id `useColumnPrefs` stores in the URL. Cells are plain renderers and never call a hook —
 * anything hook-derived is resolved by CampaignsPanel and closed over via `CampaignsColumnDeps`,
 * the same rule the sibling tab's column file follows.
 */
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton } from "@mui/material";
import type { CSSProperties, ReactNode } from "react";
import StatusBadge, { type StatusTone } from "@/components/StatusBadge";
import {
  type AdminCampaignColumnId,
  COLUMN_LABELS,
  EMPTY_CELL,
  FIXED_COLUMN_LABELS,
} from "@/constants/adminCampaigns";
import type {
  AdminCampaignRow,
  AdminCampaignStatus,
  CampaignColumn,
  CampaignsColumnDeps,
  ConfigurableCampaignColumn,
} from "@/interfaces/adminCampaigns";
import { formatDate } from "@/utils/date-format";
import { formatRate, formatUsd } from "@/utils/number-format";

const ACTION_WIDTH = 72;
const CAMPAIGN_NAME_WIDTH = 250;
const CLIENT_WIDTH = 190;

const NARROW = 120;
const MEDIUM = 150;
const WIDE = 180;

/** US formatting for every displayed figure, matching utils/number-format.ts. */
const DISPLAY_LOCALE = "en-US";

/** The dash, its column's numbers and the Client button all sit on the muted ink. */
const MUTED: CSSProperties = { color: "var(--color-graphite)" };

/** Counts and rates read as a column of digits, so they are centred under a centred heading. */
const NUMERIC_CELL: CSSProperties = { textAlign: "center", fontVariantNumeric: "tabular-nums" };
const CENTER_HEADER: CSSProperties = { textAlign: "center" };

/** Reset to plain text: both fixed cells are real buttons, styled to read as the table's own ink. */
const CAMPAIGN_LINK_STYLE: CSSProperties = {
  fontFamily: "inherit",
  fontSize: "inherit",
  fontWeight: 600,
  color: "var(--color-rodeo-black)",
  textDecoration: "underline",
  textUnderlineOffset: 2,
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  textAlign: "left",
};

const CLIENT_BUTTON_STYLE: CSSProperties = {
  ...CAMPAIGN_LINK_STYLE,
  fontWeight: 500,
  color: "var(--color-dust-ink)",
  textDecoration: "none",
};

const MENU_BUTTON_SX = {
  width: 30,
  height: 30,
  padding: 0,
  borderRadius: "8px",
  color: "var(--color-graphite)",
} as const;

/** One tone per status. Paused is a reversible hold, so it reads orange, not red like a failure. */
const STATUS_TONE: Record<AdminCampaignStatus, StatusTone> = {
  Draft: "neutral",
  Scheduled: "warning",
  Active: "success",
  Paused: "orange",
  Closed: "info",
  Cancelled: "error",
  Archived: "muted",
  Disabled: "muted",
};

/** The chip reads "Paused (sched)" or "Paused (active)" so the hold's origin shows without a click. */
function statusLabel(row: AdminCampaignRow): string {
  if (row.status !== "Paused" || row.pausedFrom === null) return row.status;
  return row.pausedFrom === "scheduled" ? "Paused (sched)" : "Paused (active)";
}

/** The muted dash a null cell shows — never an empty cell, never the word "null". */
function dash(): ReactNode {
  return <span style={MUTED}>{EMPTY_CELL}</span>;
}

function textCell(value: string | null): ReactNode {
  return value === null || value.trim() === "" ? dash() : value;
}

function dateCell(iso: string | null): ReactNode {
  return iso === null ? dash() : formatDate(iso);
}

/** A 0..1 fraction as whole percent: 0.42 → "42%". Shares the CSV's formatter, so they agree. */
function percentCell(value: number | null): ReactNode {
  return value === null ? dash() : formatRate(value);
}

function countCell(value: number): string {
  return value.toLocaleString(DISPLAY_LOCALE);
}

/**
 * The three columns that are always shown, in the order they are frozen to the left edge. They
 * are built rather than declared because each one needs a handler the panel resolves.
 */
export function buildFixedColumns(deps: CampaignsColumnDeps): CampaignColumn[] {
  return [
    {
      id: "action",
      label: FIXED_COLUMN_LABELS.action,
      sortKey: null,
      width: ACTION_WIDTH,
      pinnedLeft: 0,
      render: (row) => (
        <IconButton
          onClick={(event) => deps.onOpenMenu(row, event.currentTarget)}
          aria-label={`Actions for ${row.name}`}
          aria-haspopup="menu"
          aria-expanded={deps.openMenuRowId === row.id}
          sx={MENU_BUTTON_SX}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
    {
      id: "name",
      label: FIXED_COLUMN_LABELS.campaignName,
      sortKey: "name",
      width: CAMPAIGN_NAME_WIDTH,
      pinnedLeft: ACTION_WIDTH,
      cellStyle: { whiteSpace: "normal" },
      render: (row) =>
        row.impersonation.allowed ? (
          <button
            type="button"
            style={CAMPAIGN_LINK_STYLE}
            onClick={() => deps.onOpenCampaign(row)}
          >
            {row.name}
          </button>
        ) : (
          <span title={row.impersonation.reason ?? undefined}>{row.name}</span>
        ),
    },
    {
      id: "client",
      label: FIXED_COLUMN_LABELS.client,
      sortKey: "client",
      width: CLIENT_WIDTH,
      pinnedLeft: ACTION_WIDTH + CAMPAIGN_NAME_WIDTH,
      render: (row) => (
        <button
          type="button"
          style={CLIENT_BUTTON_STYLE}
          aria-haspopup="dialog"
          aria-expanded={deps.openClientRowId === row.id}
          onClick={(event) => deps.onOpenClient(row, event.currentTarget)}
        >
          {row.organization}
        </button>
      ),
    },
  ];
}

/**
 * Every configurable column, keyed by id so `applyPrefs` can project the user's arrangement onto
 * it. The 13 are all visible by default; the order shown is the ticket's, which the
 * panel passes to `useColumnPrefs` as both the full id set and the default order.
 */
export const CONFIGURABLE_COLUMNS: Record<AdminCampaignColumnId, ConfigurableCampaignColumn> = {
  account_lead: {
    id: "account_lead",
    label: COLUMN_LABELS.account_lead,
    sortKey: "account_lead",
    width: WIDE,
    render: (row) => textCell(row.accountLead),
  },
  collection_value: {
    id: "collection_value",
    label: COLUMN_LABELS.collection_value,
    sortKey: "collection_value",
    width: MEDIUM,
    cellStyle: NUMERIC_CELL,
    headerStyle: CENTER_HEADER,
    render: (row) => formatUsd(row.collectionValue),
  },
  status: {
    id: "status",
    label: COLUMN_LABELS.status,
    sortKey: "status",
    width: NARROW,
    render: (row) => <StatusBadge label={statusLabel(row)} tone={STATUS_TONE[row.status]} />,
  },
  recipients: {
    id: "recipients",
    label: COLUMN_LABELS.recipients,
    sortKey: "recipients",
    width: NARROW,
    cellStyle: NUMERIC_CELL,
    headerStyle: CENTER_HEADER,
    render: (row) => countCell(row.recipients),
  },
  scheduled_at: {
    id: "scheduled_at",
    label: COLUMN_LABELS.scheduled_at,
    sortKey: "scheduled_at",
    width: MEDIUM,
    render: (row) => dateCell(row.scheduledAt),
  },
  expiry: {
    id: "expiry",
    label: COLUMN_LABELS.expiry,
    sortKey: "expiry",
    width: MEDIUM,
    render: (row) => dateCell(row.expiry),
  },
  campaign_type: {
    id: "campaign_type",
    label: COLUMN_LABELS.campaign_type,
    sortKey: "campaign_type",
    width: MEDIUM,
    render: (row) => row.campaignType,
  },
  send_method: {
    id: "send_method",
    label: COLUMN_LABELS.send_method,
    sortKey: "send_method",
    width: MEDIUM,
    render: (row) => row.sendMethod,
  },
  open_rate: {
    id: "open_rate",
    label: COLUMN_LABELS.open_rate,
    sortKey: "open_rate",
    width: NARROW,
    cellStyle: NUMERIC_CELL,
    headerStyle: CENTER_HEADER,
    render: (row) => percentCell(row.openRate),
  },
  redemption_rate: {
    id: "redemption_rate",
    label: COLUMN_LABELS.redemption_rate,
    sortKey: "redemption_rate",
    width: WIDE,
    cellStyle: NUMERIC_CELL,
    headerStyle: CENTER_HEADER,
    render: (row) => percentCell(row.redemptionRate),
  },
  bounces: {
    id: "bounces",
    label: COLUMN_LABELS.bounces,
    sortKey: "bounces",
    width: NARROW,
    cellStyle: NUMERIC_CELL,
    headerStyle: CENTER_HEADER,
    render: (row) => countCell(row.bounces),
  },
  scheduled_reminders: {
    id: "scheduled_reminders",
    label: COLUMN_LABELS.scheduled_reminders,
    sortKey: "scheduled_reminders",
    width: WIDE,
    cellStyle: NUMERIC_CELL,
    headerStyle: CENTER_HEADER,
    render: (row) => countCell(row.scheduledReminders),
  },
  thank_you_messages: {
    id: "thank_you_messages",
    label: COLUMN_LABELS.thank_you_messages,
    sortKey: "thank_you_messages",
    width: WIDE,
    cellStyle: NUMERIC_CELL,
    headerStyle: CENTER_HEADER,
    render: (row) => countCell(row.thankYouMessages),
  },
};
